import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "components/ui/button";
import useMyTrainingSummary from "hooks/useMyTrainingSummary";
import { buildFlowPath } from "utils/flowNavigation";
import { CourseOverview } from "./CourseOverview";
import { courseEligibility } from "./coursePresentation";
import "./MyTraining.css";

export function CourseDetails() {
  const { courseId: routeCourseId } = useParams<{ courseId: string }>();
  const parsedId = Number(routeCourseId);
  const courseId = Number.isSafeInteger(parsedId) && parsedId > 0 ? parsedId : undefined;
  const { data, loading, error, refetch } = useMyTrainingSummary(courseId);
  const location = useLocation();
  const navigate = useNavigate();
  const page = useRef<HTMLElement>(null);
  const [pendingStart, setPendingStart] = useState<number | null>(null);
  const state: unknown = location.state;
  const startCourse = Boolean(state && typeof state === "object" && "startCourse" in state && state.startCourse);
  const summary = data?.assigned_courses.find((course) => course.id === courseId);
  const progress = courseId && !error && !data?.course_error && !data?.course_errors?.[courseId]
    ? data?.all_course_progress[courseId] : undefined;
  const lockedNotice = Boolean(state && typeof state === "object" && "courseLocked" in state && state.courseLocked && summary && courseEligibility(summary, progress)?.is_locked);

  useLayoutEffect(() => {
    if (!loading) page.current?.querySelector<HTMLElement>("h1")?.focus({ preventScroll: true });
  }, [courseId, loading]);

  useEffect(() => {
    if (!startCourse) return;
    // Keep a launch pending only while this page remains mounted. Consume the
    // history intent immediately so Back during loading, then Forward, is safe.
    setPendingStart(courseId ?? null);
    const nextState = state && typeof state === "object" ? { ...state, startCourse: false } : null;
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: nextState });
  }, [startCourse, courseId, state, navigate, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!pendingStart || loading) return;
    setPendingStart(null);
    if (pendingStart !== courseId) return;
    const flow = summary && !courseEligibility(summary, progress)?.is_locked
      ? progress?.flows.find((item) => item.is_current && !item.is_locked) : undefined;
    if (summary && flow) navigate(buildFlowPath({ courseId: summary.id, flowName: flow.flow_details.name }));
  }, [pendingStart, loading, courseId, navigate, summary, progress]);

  const retry = () => { void refetch().catch(() => undefined); };
  return <section ref={page} className="learner-courses course-detail-page" aria-label={summary ? `${summary.name} course details` : "Course details"}>
    {loading ? <div className="course-empty" role="status">Loading course details…</div>
      : error && !summary ? <div className="course-empty" role="alert"><h1 tabIndex={-1}>We couldn’t load this course</h1><p>Please try again in a moment.</p><Button size="auto" onClick={retry}>Try again</Button><Link to="/training#courses" className="course-back"><ArrowLeft size={16} />Back to courses</Link></div>
      : !summary ? <div className="course-empty" role="status"><h1 tabIndex={-1}>Course unavailable</h1><p>This course isn’t available in your assignments. Return to My Courses or ask your administrator for access.</p><Link to="/training#courses" className="course-back"><ArrowLeft size={16} />Back to courses</Link></div>
      : <>{lockedNotice && <p className="course-notice" role="status">This module belongs to a locked course. Complete the prerequisites below to continue.</p>}<CourseOverview key={summary.id} summary={summary} progress={progress} onRetry={retry} /></>}
  </section>;
}
